import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { 
  Shield, Lock, Eye, Database, UserCheck, Bell, Globe, Mail,
  Building2, FileCheck, Users, Server, Trash2, AlertCircle, Handshake
} from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Shield className="h-16 w-16 text-white mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Privacy Policy & Trust
          </h1>
          <p className="text-white/90">
            Last Updated: January 2025
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Trust & Compliance Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8 text-emerald-700">Trust & Compliance</h2>
          
          <Card className="mb-8 border-emerald-200 border-2">
            <CardContent className="p-8">
              <p className="text-gray-700 leading-relaxed text-lg">
                We are committed to protecting student privacy, maintaining data security, and supporting 
                compliance with applicable education and data protection laws. Our platform is designed to 
                responsibly support international students throughout the higher education journey—from 
                school discovery to enrollment preparation.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Our Role */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Building2 className="h-6 w-6 text-emerald-600" />
              <h2 className="text-2xl font-bold">Our Role</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We provide a centralized platform that helps international students:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Discover and compare colleges aligned with their academic and personal goals</li>
              <li>Manage application materials</li>
              <li>Coordinate with counselors and institutions</li>
              <li>Receive guidance on admissions and visa preparation processes</li>
            </ul>
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 font-medium">
                <AlertCircle className="h-5 w-5 inline mr-2" />
                We do not act as a university, admissions authority, or immigration law firm.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Student Data Protection */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="h-6 w-6 text-emerald-600" />
              <h2 className="text-2xl font-bold">Student Data Protection</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We collect and process student information only as necessary to provide our services. Student data is:
            </p>
            <ul className="space-y-3 ml-4">
              <li className="flex items-start gap-3">
                <span className="text-emerald-600 font-bold">✓</span>
                <span className="text-gray-700"><strong>Never sold, rented, or used for advertising</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-emerald-600 font-bold">✓</span>
                <span className="text-gray-700">Used solely to support educational and application-related services</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-emerald-600 font-bold">✓</span>
                <span className="text-gray-700">Accessed only by authorized users with a legitimate purpose</span>
              </li>
            </ul>
            <p className="text-gray-700 mt-4 p-4 bg-emerald-50 rounded-lg">
              Our practices follow principles of <strong>data minimization</strong>, <strong>purpose limitation</strong>, and <strong>transparency</strong>.
            </p>
          </CardContent>
        </Card>

        {/* FERPA Compliance */}
        <Card className="mb-8 border-blue-200 border-2">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <FileCheck className="h-6 w-6 text-blue-600" />
              <h2 className="text-2xl font-bold">FERPA Compliance (U.S. Institutions)</h2>
            </div>
            <p className="text-gray-700 mb-4">
              When working with U.S. colleges and universities, we comply with the Family Educational Rights and Privacy Act (FERPA).
            </p>
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <p className="text-gray-700">
                Where applicable, we act as a <strong>school official with a legitimate educational interest</strong>, 
                performing services institutions would otherwise perform themselves, under written agreements.
              </p>
            </div>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Education records are used only for authorized educational purposes</li>
              <li>Records are not redisclosed except as permitted by law and institutional agreement</li>
            </ul>
            <p className="text-gray-600 mt-4 text-sm italic">
              FERPA applies specifically to U.S. educational institutions; however, we apply FERPA-aligned 
              safeguards across our platform as a baseline standard.
            </p>
          </CardContent>
        </Card>

        {/* International Data Protection */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="h-6 w-6 text-teal-600" />
              <h2 className="text-2xl font-bold">International Data Protection</h2>
            </div>
            <p className="text-gray-700 mb-4">
              Because we serve international students, we design our systems to align with global privacy expectations, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Transparent notice of data use</li>
              <li>User access and correction rights</li>
              <li>Secure cross-border data handling</li>
            </ul>
            <p className="text-gray-700 mt-4">
              We process personal data in accordance with applicable local laws and contractual obligations with partner institutions.
            </p>
          </CardContent>
        </Card>

        {/* Security Safeguards */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="h-6 w-6 text-emerald-600" />
              <h2 className="text-2xl font-bold">Security Safeguards</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We implement reasonable administrative, technical, and physical safeguards, including:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <Server className="h-5 w-5 text-emerald-600 mb-2" />
                <p className="font-medium">Encryption in transit and at rest</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <Users className="h-5 w-5 text-emerald-600 mb-2" />
                <p className="font-medium">Role-based access controls</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <Lock className="h-5 w-5 text-emerald-600 mb-2" />
                <p className="font-medium">Secure authentication</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <Eye className="h-5 w-5 text-emerald-600 mb-2" />
                <p className="font-medium">Monitoring and audit logging</p>
              </div>
            </div>
            <p className="text-gray-700 mt-4">
              Access to student information is limited to individuals with a legitimate educational or operational need.
            </p>
          </CardContent>
        </Card>

        {/* Data Retention & Deletion */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Trash2 className="h-6 w-6 text-teal-600" />
              <h2 className="text-2xl font-bold">Data Retention & Deletion</h2>
            </div>
            <p className="text-gray-700">
              Student data is retained only for as long as necessary to provide services or meet legal and 
              contractual obligations. Upon request, or upon termination of services, data is returned or 
              securely deleted in accordance with our retention policies.
            </p>
          </CardContent>
        </Card>

        {/* Third-Party Services */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Server className="h-6 w-6 text-cyan-600" />
              <h2 className="text-2xl font-bold">Third-Party Services</h2>
            </div>
            <p className="text-gray-700">
              We use vetted third-party service providers (such as hosting and security services) to support 
              platform operations. All service providers are contractually obligated to maintain confidentiality 
              and data protection standards consistent with our commitments.
            </p>
          </CardContent>
        </Card>

        {/* Admissions & Visa Guidance Disclaimer */}
        <Card className="mb-8 border-amber-200 border-2">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-amber-600" />
              <h2 className="text-2xl font-bold">Admissions & Visa Guidance Disclaimer</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We provide informational and process guidance related to admissions and student visa preparation. 
              <strong> We do not provide legal advice, represent students before immigration authorities, or 
              guarantee admissions or visa outcomes.</strong>
            </p>
            <p className="text-gray-700 bg-amber-50 p-4 rounded-lg">
              Students are encouraged to consult official university communications and government immigration 
              authorities for final decisions.
            </p>
          </CardContent>
        </Card>

        {/* Institution Partnerships Section */}
        <div className="mb-12 mt-16">
          <h2 className="text-3xl font-bold text-center mb-8 text-emerald-700">Institution Partnerships</h2>
        </div>

        {/* Partnering with Global Institutions */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Handshake className="h-6 w-6 text-emerald-600" />
              <h2 className="text-2xl font-bold">Partnering with Global Institutions</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We partner with colleges and universities to help international students identify appropriate 
              academic programs and navigate application processes in a structured, transparent, and secure way.
            </p>
            <p className="text-gray-700">
              Our platform is designed to support institutional workflows while respecting institutional 
              autonomy and compliance requirements.
            </p>
          </CardContent>
        </Card>

        {/* FERPA-Aligned Institutional Model */}
        <Card className="mb-8 border-blue-200 border-2">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <FileCheck className="h-6 w-6 text-blue-600" />
              <h2 className="text-2xl font-bold">FERPA-Aligned Institutional Model</h2>
            </div>
            <p className="text-gray-700 mb-4">
              For U.S. institutions, we operate as a <strong>school official with legitimate educational interest</strong> under 
              FERPA when authorized by written agreement.
            </p>
            <p className="text-gray-700 mb-4">This means:</p>
            <ul className="space-y-3 ml-4">
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold">•</span>
                <span className="text-gray-700">We perform services institutions would otherwise perform internally</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold">•</span>
                <span className="text-gray-700">We use education records solely for authorized educational purposes</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold">•</span>
                <span className="text-gray-700">Institutions maintain control over data access and use</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold">•</span>
                <span className="text-gray-700">Student data is not independently commercialized</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Institutional Control & Transparency */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="h-6 w-6 text-teal-600" />
              <h2 className="text-2xl font-bold">Institutional Control & Transparency</h2>
            </div>
            <p className="text-gray-700 mb-4">Partner institutions control:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>What data is shared</li>
              <li>Which users have access</li>
              <li>How long data is retained</li>
            </ul>
            <p className="text-gray-700 mt-4">
              We provide transparency into our data practices and support institutional compliance reviews as needed.
            </p>
          </CardContent>
        </Card>

        {/* Secure Role-Based Access */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Users className="h-6 w-6 text-emerald-600" />
              <h2 className="text-2xl font-bold">Secure Role-Based Access</h2>
            </div>
            <p className="text-gray-700 mb-4">Our platform supports:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Role-based permissions for students, counselors, and admissions staff</li>
              <li>Least-privilege access controls</li>
              <li>Secure authentication standards</li>
            </ul>
            <p className="text-gray-700 mt-4 p-4 bg-emerald-50 rounded-lg">
              Only authorized users with a legitimate educational interest can access student records.
            </p>
          </CardContent>
        </Card>

        {/* Data Protection & Agreements */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <FileCheck className="h-6 w-6 text-cyan-600" />
              <h2 className="text-2xl font-bold">Data Protection & Agreements</h2>
            </div>
            <p className="text-gray-700 mb-4">We offer institutional documentation, including:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>FERPA-aligned contractual terms (where applicable)</li>
              <li>Data protection and confidentiality commitments</li>
              <li>Subprocessor disclosures</li>
              <li>Retention and deletion provisions</li>
            </ul>
            <p className="text-gray-700 mt-4">
              We can support custom agreements to align with institutional policies and regional requirements.
            </p>
          </CardContent>
        </Card>

        {/* Supporting, Not Replacing */}
        <Card className="mb-8 bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Handshake className="h-6 w-6 text-emerald-600" />
              <h2 className="text-2xl font-bold">Supporting, Not Replacing, Institutional Responsibility</h2>
            </div>
            <p className="text-gray-700 text-lg">
              Institutions retain responsibility for admissions decisions and legal compliance. Our role is to 
              support institutional processes through <strong>secure systems</strong>, <strong>transparent practices</strong>, 
              and <strong>responsible data handling</strong>.
            </p>
          </CardContent>
        </Card>

        {/* Original Privacy Policy Sections */}
        <div className="mb-12 mt-16">
          <h2 className="text-3xl font-bold text-center mb-8 text-emerald-700">Privacy Policy Details</h2>
        </div>

        {/* Introduction */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              At Wayo ("we," "us," or "our"), we are committed to protecting your privacy and ensuring 
              the security of your personal information. This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you use our college application platform.
            </p>
          </CardContent>
        </Card>

        {/* Information We Collect */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Database className="h-6 w-6 text-emerald-600" />
              <h2 className="text-2xl font-bold">Information We Collect</h2>
            </div>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">Personal Information</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Name, email address, and contact information</li>
              <li>Date of birth and demographic information</li>
              <li>Educational history and academic records</li>
              <li>Standardized test scores (SAT, ACT, etc.)</li>
              <li>Extracurricular activities and achievements</li>
              <li>Essays and personal statements</li>
              <li>Financial information for aid applications</li>
              <li>Family information as required by applications</li>
            </ul>

            <h3 className="text-lg font-semibold mt-6 mb-3">Automatically Collected Information</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Device information (browser type, operating system)</li>
              <li>IP address and general location data</li>
              <li>Usage patterns and interaction with our platform</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </CardContent>
        </Card>

        {/* How We Use Your Information */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="h-6 w-6 text-teal-600" />
              <h2 className="text-2xl font-bold">How We Use Your Information</h2>
            </div>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Process and submit your college applications</li>
              <li>Share application materials with colleges you select</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Send important updates about your applications</li>
              <li>Improve our platform and develop new features</li>
              <li>Analyze usage patterns to enhance user experience</li>
              <li>Comply with legal obligations and protect our rights</li>
              <li>Detect and prevent fraud or unauthorized access</li>
            </ul>
          </CardContent>
        </Card>

        {/* Information Sharing */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="h-6 w-6 text-cyan-600" />
              <h2 className="text-2xl font-bold">Information Sharing</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We share your information only in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>With Colleges:</strong> When you submit an application, your information is shared with the selected institutions</li>
              <li><strong>With Counselors:</strong> If you grant access, your school counselor may view your application progress</li>
              <li><strong>Service Providers:</strong> Trusted third parties who assist in operating our platform</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our legal rights</li>
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information</li>
            </ul>
            <p className="text-gray-700 mt-4 p-4 bg-emerald-50 rounded-lg">
              <strong>We never sell your personal information to third parties for marketing purposes.</strong>
            </p>
          </CardContent>
        </Card>

        {/* Your Rights */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="h-6 w-6 text-teal-600" />
              <h2 className="text-2xl font-bold">Your Rights</h2>
            </div>
            <p className="text-gray-700 mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Deletion:</strong> Request deletion of your data (subject to legal obligations)</li>
              <li><strong>Portability:</strong> Receive your data in a portable format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Restrict Processing:</strong> Limit how we use your data</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To exercise these rights, contact us at <a href="mailto:privacy@wayo.com" className="text-emerald-600 hover:underline">privacy@wayo.com</a>
            </p>
          </CardContent>
        </Card>

        {/* Cookies */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Bell className="h-6 w-6 text-cyan-600" />
              <h2 className="text-2xl font-bold">Cookies & Tracking</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Keep you signed in to your account</li>
              <li>Remember your preferences and settings</li>
              <li>Understand how you use our platform</li>
              <li>Improve our services based on usage patterns</li>
            </ul>
            <p className="text-gray-700 mt-4">
              You can manage cookie preferences through your browser settings.
            </p>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="mb-8 bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="h-6 w-6 text-emerald-600" />
              <h2 className="text-2xl font-bold">Contact Us</h2>
            </div>
            <p className="text-gray-700 mb-4">
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="text-gray-700 space-y-2">
              <p><strong>Privacy:</strong> <a href="mailto:privacy@wayo.com" className="text-emerald-600 hover:underline">privacy@wayo.com</a></p>
              <p><strong>Support:</strong> <a href="mailto:support@wayo.com" className="text-emerald-600 hover:underline">support@wayo.com</a></p>
              <p><strong>Membership:</strong> <a href="mailto:member@wayo.com" className="text-emerald-600 hover:underline">member@wayo.com</a></p>
            </div>
          </CardContent>
        </Card>

        {/* Updates Notice */}
        <div className="text-center text-gray-500 text-sm">
          <p>This Privacy Policy may be updated periodically. We will notify you of any material changes.</p>
          <p className="mt-2">© 2026 Wayo. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
